import { Check } from "lucide-react";
import { useState } from "react";
import { ProductScreen } from "../components/ProductScreen.js";
import type { ScreenName } from "../components/ProductScreen.js";

interface OrganizerView {
  readonly key: ScreenName;
  readonly title: string;
  readonly text: string;
  readonly bullets: readonly string[];
}

const VIEWS: readonly OrganizerView[] = [
  {
    key: "overview",
    title: "The whole picture",
    text: "See your event's progress, recent gifts, and what needs attention in one place.",
    bullets: [
      "A clear view of giving",
      "Online and posted offline gifts",
      "Upcoming events at a glance",
    ],
  },
  {
    key: "live",
    title: "The energy in the room",
    text: "Bring the big screen, audience questions, and live participation together as the evening unfolds.",
    bullets: [
      "Control what's on the big screen",
      "Make space for questions and polls",
      "Keep your fundraising appeal moving",
    ],
  },
  {
    key: "gifts",
    title: "Clarity after the applause",
    text: "Keep gifts, processing payments, and pledges distinct, with the details your team needs to follow up.",
    bullets: [
      "Know the status of each gift",
      "Find receipts and payment details",
      "Export records for accounting",
    ],
  },
];

export function Organizers() {
  const [view, setView] = useState(0);
  const active = VIEWS[view]!;

  function onTabKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    let next = index;
    if (event.key === "ArrowRight") next = (index + 1) % VIEWS.length;
    else if (event.key === "ArrowLeft") next = (index + VIEWS.length - 1) % VIEWS.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = VIEWS.length - 1;
    else return;
    event.preventDefault();
    setView(next);
    document.getElementById(`organizer-tab-${VIEWS[next]!.key}`)?.focus();
  }

  return (
    <section id="organizers" className="organizers">
      <div className="page">
        <div className="section__heading">
          <div>
            <p className="overline">
              <span className="overline__rule" aria-hidden="true" />
              Behind every great giving moment
            </p>
            <h2>
              More connection.
              <br />
              <em>Less coordination.</em>
            </h2>
          </div>
          <p>
            Stay present with your community. GiveHere brings your event and giving tools into one
            organized workspace.
          </p>
        </div>

        <div className="organizers__tabs" role="tablist" aria-label="Organizer tools">
          {VIEWS.map((v, index) => (
            <button
              key={v.key}
              className="organizers__tab"
              id={`organizer-tab-${v.key}`}
              role="tab"
              aria-selected={index === view}
              aria-controls={`organizer-panel-${v.key}`}
              tabIndex={index === view ? 0 : -1}
              onClick={() => setView(index)}
              onKeyDown={(event) => onTabKeyDown(event, index)}
            >
              <span>0{index + 1}</span>
              {v.title}
            </button>
          ))}
        </div>

        <div
          role="tabpanel"
          id={`organizer-panel-${active.key}`}
          aria-labelledby={`organizer-tab-${active.key}`}
          className="organizers__panel"
        >
          <div className="organizers__description">
            <p key={view}>{active.text}</p>
            <ul className="organizers__list">
              {active.bullets.map((bullet) => (
                <li key={bullet}>
                  <Check size={15} />
                  {bullet}
                </li>
              ))}
            </ul>
          </div>
          <div className="organizers__preview">
            <div className="browser-bar">
              <span className="browser-bar__dot" />
              <span className="browser-bar__dot" />
              <span className="browser-bar__dot" />
              <p className="browser-bar__title">GiveHere · {active.title}</p>
              <span className="preview-tag">Example workspace</span>
            </div>
            <div className="organizers__viewport">
              <ProductScreen screen={active.key} />
            </div>
          </div>
        </div>
        <p className="organizers__caption">
          A closer look at the organizer experience. Screens show illustrative data.
        </p>
      </div>
    </section>
  );
}
